import { computed, Injectable } from '@angular/core';
import { AttendeeDetailsModalService } from '../attendee-details-modal.service';
import { firstValueFrom, map, mergeMap, Observable, shareReplay } from 'rxjs';
import {
  AssociationKeys,
  AttendeeType,
  ChangeSourceType,
  Conference,
  ConferenceKeys,
  ConferenceRegistrationCategoryName,
  ConferenceRegistrationCommonTask,
  Excursion,
  ExcursionStatistic,
  GuestData,
  GuestKeys,
  Registrant,
  RegistrantKeys,
  RegistrantModelKeys,
} from 'ag-common-lib/public-api';
import {
  ExcursionInfoTableData,
  ExcursionInfoTableDataKeys,
} from 'ag-common-svc/lib/components/excursions-info-table/excursions-info-table.models';

import { convertSelectedExcursionsToList, getExcursionsMap } from 'ag-common-svc/lib/utils/excursions-data.utils';
import { ConferenceRegistrantsService, ConferenceService } from 'ag-common-svc/public-api';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { AttendeeGuestsService } from '../attendee-guests/attendee-guests.service';
import { get } from 'lodash';
import { ChairmanExtensionConfigurationKeys } from 'ag-common-lib/lib/models/registration/conference-chairman-extension';
import { RegistrantChairmanExtensionKeys } from 'ag-common-lib/lib/models/registration/registrant-chairman-extension';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class AttendeeExcursionsService {
  private excursionStatisticsMap$: Observable<Map<string, ExcursionStatistic>> =
    this.attendeeDetailsModalService.conferenceDbId$.pipe(
      mergeMap(conferenceDbId => this.conferenceService.getExcursionStatistics(conferenceDbId)),
    );

  excursionStatisticsMapSig = toSignal(this.excursionStatisticsMap$, { initialValue: new Map() });

  excursionsTaskStatus$: Observable<ConferenceRegistrationCommonTask>;
  assignOwnerList$ = this.attendeeDetailsModalService.assignOwnerList$;

  mainEventExcursionsBudgetSig = computed(() => {
    const conference = this.attendeeDetailsModalService.conferenceSig();
    const excursionsBudget = conference?.[ConferenceKeys.excursionsBudget];

    return excursionsBudget;
  });

  chairmanExtExcursionsBudgetSig = computed(() => {
    const conference = this.attendeeDetailsModalService.conferenceSig();
    const excursionsBudget =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[
        ChairmanExtensionConfigurationKeys.excursionsBudget
      ];

    return excursionsBudget;
  });

  mainEventExcursionsSig = computed(() => {
    const conference = this.attendeeDetailsModalService.conferenceSig();
    const excursions = conference?.[ConferenceKeys.excursions] ?? [];

    return excursions;
  });

  chairmanExtExcursionsSig = computed(() => {
    const conference = this.attendeeDetailsModalService.conferenceSig();
    const excursions =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.excursions] ??
      [];

    return excursions;
  });

  mainEventSelectedExcursionsDataSourceSig = computed(() => {
    const conference = this.attendeeDetailsModalService.conferenceSig();
    const excursions = conference?.[ConferenceKeys.excursions] ?? [];
    const registrant = this.attendeeDetailsModalService.registrantSig();
    const registrantGuests = this.attendeeGuestsService.registrantActiveGuestsSig() ?? [];

    return this.getExcursionsDataSource({
      registrant,
      registrantGuests,
      excursions,
      registrantSelectedExcursionsKey: RegistrantKeys.selectedExcursions,
      guestSelectedExcursionsKey: GuestKeys.selectedExcursions,
    });
  });

  chairmanExtSelectedExcursionsDataSourceSig = computed(() => {
    const conference = this.attendeeDetailsModalService.conferenceSig();
    const excursions =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.excursions] ??
      [];
    const registrant = this.attendeeDetailsModalService.registrantSig();
    const registrantGuests = this.attendeeGuestsService.registrantActiveGuestsSig() ?? [];

    return this.getExcursionsDataSource({
      registrant,
      registrantGuests,
      excursions,
      registrantSelectedExcursionsKey: this.CHAIRMAN_EXT_REGISTRANT_SELECTED_EXCURSIONS_KEY,
      guestSelectedExcursionsKey: this.CHAIRMAN_EXT_GUEST_SELECTED_EXCURSIONS_KEY,
    });
  });

  readonly CHAIRMAN_EXT_REGISTRANT_SELECTED_EXCURSIONS_KEY = [
    RegistrantKeys.chairmanExtension,
    RegistrantChairmanExtensionKeys.selectedExcursions,
  ];

  readonly CHAIRMAN_EXT_GUEST_SELECTED_EXCURSIONS_KEY = GuestKeys.chairmanExtSelectedExcursions;

  constructor(
    private conferenceService: ConferenceService,
    private attendeeGuestsService: AttendeeGuestsService,
    private attendeeDetailsModalService: AttendeeDetailsModalService,
    private conferenceRegistrantsService: ConferenceRegistrantsService,
  ) {
    this.excursionsTaskStatus$ = this.attendeeDetailsModalService.registrant$.pipe(
      map(registrant => {
        return registrant?.[RegistrantModelKeys.task]?.[ConferenceRegistrationCategoryName.registrantExcursionCategory];
      }),
      shareReplay(1),
    );
  }

  updateExcursionsTask = async (task: Partial<ConferenceRegistrationCommonTask>) => {
    const registrant = await firstValueFrom(this.attendeeDetailsModalService.registrant$);
    const registrantId = registrant[BaseModelKeys.dbId];
    const conferenceDbId = registrant[RegistrantModelKeys.conferenceDbId];
    const sourceType = ChangeSourceType.regularArm;

    const update = {
      [RegistrantModelKeys.task]: {
        [ConferenceRegistrationCategoryName.registrantExcursionCategory]: task,
      },
    };

    await this.conferenceRegistrantsService.update(conferenceDbId, registrantId, update, sourceType, false);
  };

  private getExcursionsDataSource({
    registrant,
    registrantGuests,
    excursions,
    registrantSelectedExcursionsKey,
    guestSelectedExcursionsKey,
  }: {
    registrant: Registrant;
    registrantGuests: GuestData[];
    excursions: Excursion[];
    registrantSelectedExcursionsKey: string | string[];
    guestSelectedExcursionsKey: string;
  }) {
    const conferenceDbId = this.attendeeDetailsModalService.conferenceDbIdSig();
    const registrantDbId = this.attendeeDetailsModalService.registrantDbIdSig();
    const registrationData = registrant?.[RegistrantModelKeys.data];
    const registrantSelectedExcursions = get(registrationData, registrantSelectedExcursionsKey);
    const { excursionsDates, excursionsMap } = getExcursionsMap(excursions);
    const list: ExcursionInfoTableData[] = convertSelectedExcursionsToList(
      excursionsMap,
      registrantSelectedExcursions,
      {
        [ExcursionInfoTableDataKeys.conferenceDbId]: conferenceDbId,
        [ExcursionInfoTableDataKeys.registrantDbId]: registrantDbId,
        [ExcursionInfoTableDataKeys.attendeeId]: registrantDbId,
        [ExcursionInfoTableDataKeys.fullName]: [
          registrationData[RegistrantKeys.firstName],
          registrationData[RegistrantKeys.lastName],
        ]
          .filter(Boolean)
          .join(' '),
        [ExcursionInfoTableDataKeys.attendeeType]: AttendeeType.Invitee,
        [ExcursionInfoTableDataKeys.attendeeDob]: registrationData[RegistrantKeys.dob],
      },
      excursionsDates,
    );

    registrantGuests.forEach(guest => {
      const guestSelectedExcursions = get(guest, guestSelectedExcursionsKey);
      const guestExcursionsList = convertSelectedExcursionsToList(
        excursionsMap,
        guestSelectedExcursions,
        {
          [ExcursionInfoTableDataKeys.conferenceDbId]: conferenceDbId,
          [ExcursionInfoTableDataKeys.registrantDbId]: registrantDbId,
          [ExcursionInfoTableDataKeys.attendeeId]: guest?.[BaseModelKeys.dbId],
          [ExcursionInfoTableDataKeys.fullName]: [guest[AssociationKeys.firstName], guest[AssociationKeys.lastName]]
            .filter(Boolean)
            .join(' '),
          [ExcursionInfoTableDataKeys.attendeeType]: AttendeeType.Guest,
          [ExcursionInfoTableDataKeys.attendeeDob]: guest[AssociationKeys.dob],
        },
        excursionsDates,
      );

      list.push(...guestExcursionsList);
    });

    return list;
  }
}
